import { FC, useState, useEffect, lazy, Suspense } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Spinner } from '@chakra-ui/react';
import { Plus, TrashSimple, Barbell } from '@phosphor-icons/react';
import GenericModalV2 from '../../../shared/GenericModal/GenericModal';
import { useEndpoints } from '../../../hooks/useEndpoints';
import {
  IFieldBlock,
  IFieldBlockExercise,
  TacticalPhase,
  FieldBlockType,
} from '../../../models/field-programming';
import { TacticalSchema } from '../../../models/tactical-schema';
import styles from './FieldBlockBuilderModal.module.scss';

const TacticalBoardSetupModal = lazy(() =>
  import('../TacticalBoard/TacticalBoardSetupModal').then((m) => ({
    default: m.TacticalBoardSetupModal,
  })),
);
const TacticalBoardEditor = lazy(() => import('../TacticalBoard/TacticalBoardEditor'));

interface Props {
  block?: IFieldBlock;
  isOpen: boolean;
  onClose: () => void;
}

const PHASES: { value: TacticalPhase; label: string }[] = [
  { value: 'warmup', label: 'Riscaldamento' },
  { value: 'activation', label: 'Attivazione' },
  { value: 'athletic', label: 'Atletico' },
  { value: 'technical', label: 'Tecnica' },
  { value: 'situational', label: 'Situazionale' },
  { value: 'match', label: 'Partita' },
  { value: 'compensative', label: 'Compensativo' },
  { value: 'cool_down', label: 'Defaticamento' },
];

const PHASE_COLORS: Record<TacticalPhase, string> = {
  warmup: '#f59e0b',
  activation: '#fb923c',
  athletic: '#ef4444',
  technical: '#8b5cf6',
  situational: '#6366f1',
  match: '#10b981',
  compensative: '#14b8a6',
  cool_down: '#0ea5e9',
};

const TYPES: { value: FieldBlockType; label: string }[] = [
  { value: 'sequential', label: 'Sequenziale' },
  { value: 'parallel', label: 'Parallelo' },
  { value: 'rotation', label: 'Rotazione' },
  { value: 'break', label: 'Pausa' },
  { value: 'physical', label: 'Fisico' },
];

const FieldBlockBuilderModal: FC<Props> = ({ block, isOpen, onClose }) => {
  const api = useEndpoints();
  const qc = useQueryClient();
  const isEdit = !!block;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tacticalPhase, setTacticalPhase] = useState<TacticalPhase>('warmup');
  const [blockType, setBlockType] = useState<FieldBlockType>('sequential');
  const [defaultDurationMinutes, setDuration] = useState<number | undefined>();
  const [exercises, setExercises] = useState<IFieldBlockExercise[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [tacticalSchemaId, setTacticalSchemaId] = useState<string | undefined>(
    block?.tacticalSchemaId,
  );
  const [setupOpen, setSetupOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorInitial, setEditorInitial] = useState<TacticalSchema | null>(null);

  const exercisesQ = useQuery({
    queryKey: ['fieldProgramming', 'exercises', { limit: 100 }],
    queryFn: () =>
      api.fieldProgramming.exercises.list({ limit: 100 }).then((r) => r.data.docs),
    enabled: isOpen,
  });

  useEffect(() => {
    if (!isOpen) return;
    setTagInput('');
    if (block) {
      setName(block.name);
      setDescription(block.description ?? '');
      setTacticalPhase(block.tacticalPhase);
      setBlockType(block.blockType);
      setDuration(block.defaultDurationMinutes);
      setExercises(block.exercises ?? []);
      setTags(block.tags ?? []);
      setTacticalSchemaId(block.tacticalSchemaId);
    } else {
      setName('');
      setDescription('');
      setTacticalPhase('warmup');
      setBlockType('sequential');
      setDuration(undefined);
      setExercises([]);
      setTags([]);
      setTacticalSchemaId(undefined);
    }
  }, [block, isOpen]);

  const saveMut = useMutation({
    mutationFn: (body: Partial<IFieldBlock>) =>
      block
        ? api.fieldProgramming.blocks.update(block._id, body).then((r) => r.data)
        : api.fieldProgramming.blocks.create(body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fieldProgramming', 'blocks'] });
      onClose();
    },
  });

  const phaseColor = PHASE_COLORS[tacticalPhase] ?? '#757575';

  const addExerciseRow = () =>
    setExercises((arr) => [
      ...arr,
      { customName: '', durationMinutes: 5, order: arr.length },
    ]);

  const updateExercise = (i: number, patch: Partial<IFieldBlockExercise>) =>
    setExercises((arr) => arr.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));

  const removeExercise = (i: number) =>
    setExercises((arr) =>
      arr.filter((_, idx) => idx !== i).map((e, idx) => ({ ...e, order: idx })),
    );

  const submit = () => {
    if (!name.trim()) return;
    saveMut.mutate({
      name: name.trim(),
      description: description.trim() || undefined,
      tacticalPhase,
      blockType,
      defaultDurationMinutes,
      exercises,
      tags,
      tacticalSchemaId,
    });
  };

  const Header = (
    <div className={styles.header}>
      <div className={styles.headerColorBar} style={{ background: phaseColor }} />
      <div className={styles.headerContent}>
        <div className={styles.headerTitleRow}>
          <div className={styles.headerIcon} style={{ background: phaseColor + '1A' }}>
            <Barbell size={20} weight="bold" style={{ color: phaseColor }} />
          </div>
          <div>
            <p className={styles.headerTitle}>
              {isEdit ? 'Modifica blocco campo' : 'Nuovo blocco campo'}
            </p>
            <p className={styles.headerSubtitle}>
              {isEdit
                ? 'Modifica esercizi, fase e parametri'
                : 'Configura fase tattica, tipo ed esercizi'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const Footer = (
    <div className={styles.footer}>
      <div />
      <div className={styles.footerActions}>
        <button
          type="button"
          className={styles.cancelBtn}
          onClick={onClose}
          disabled={saveMut.isPending}
        >
          Annulla
        </button>
        <button
          type="button"
          className={styles.saveBtn}
          onClick={submit}
          disabled={!name.trim() || saveMut.isPending}
        >
          {saveMut.isPending ? <Spinner size="xs" /> : null}
          {isEdit ? 'Aggiorna blocco' : 'Crea blocco'}
        </button>
      </div>
    </div>
  );

  return (
    <GenericModalV2
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      Header={Header}
      Footer={Footer}
      isLoading={saveMut.isPending}
    >
      <div className={styles.body}>
        {/* Info generali */}
        <div className={styles.section}>
          <p className={styles.sectionLabel}>Informazioni generali</p>
          <div className={styles.fieldsGrid}>
            <div className={styles.fieldGroup} style={{ gridColumn: '1 / -1' }}>
              <label className={styles.label}>Nome *</label>
              <input
                className={styles.input}
                placeholder="Es. Possesso 4v4 con sponde"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className={styles.fieldGroup} style={{ gridColumn: '1 / -1' }}>
              <label className={styles.label}>Descrizione</label>
              <textarea
                className={styles.textarea}
                placeholder="Descrivi finalità tecnico-tattiche, organizzazione, varianti..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>Fase tattica</label>
              <div className={styles.typeSelectWrapper}>
                <span
                  className={styles.typeColorDot}
                  style={{ background: PHASE_COLORS[tacticalPhase] }}
                />
                <select
                  className={styles.select}
                  value={tacticalPhase}
                  onChange={(e) => setTacticalPhase(e.target.value as TacticalPhase)}
                >
                  {PHASES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>Tipo blocco</label>
              <select
                className={styles.select}
                value={blockType}
                onChange={(e) => setBlockType(e.target.value as FieldBlockType)}
              >
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>Durata (min)</label>
              <input
                type="number"
                min={0}
                className={styles.input}
                placeholder="—"
                value={defaultDurationMinutes ?? ''}
                onChange={(e) =>
                  setDuration(e.target.value ? Number(e.target.value) : undefined)
                }
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>Tag</label>
              <div className={styles.tagsContainer}>
                {tags.map((t, i) => (
                  <span key={i} className={styles.tagChip}>
                    {t}
                    <button
                      type="button"
                      className={styles.tagChipRemove}
                      onClick={() => setTags(tags.filter((_, idx) => idx !== i))}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  className={styles.tagInput}
                  placeholder={tags.length === 0 ? 'Aggiungi tag...' : ''}
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
                      e.preventDefault();
                      const newTag = tagInput.trim().replace(/,$/, '');
                      if (newTag && !tags.includes(newTag)) {
                        setTags([...tags, newTag]);
                      }
                      setTagInput('');
                    }
                    if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
                      setTags(tags.slice(0, -1));
                    }
                  }}
                  onBlur={() => {
                    if (tagInput.trim()) {
                      const newTag = tagInput.trim();
                      if (!tags.includes(newTag)) setTags([...tags, newTag]);
                      setTagInput('');
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.divider} />

        {/* Lavagna tattica */}
        <div className={styles.section}>
          <p className={styles.sectionLabel}>Lavagna tattica</p>
          {!tacticalSchemaId ? (
            <button
              type="button"
              className={styles.addExerciseBtn}
              onClick={() => setSetupOpen(true)}
            >
              + Aggiungi lavagna tattica
            </button>
          ) : (
            <button
              type="button"
              className={styles.addExerciseBtn}
              onClick={async () => {
                const res = await api.tacticalSchemas.one(tacticalSchemaId);
                if (res.data) {
                  setEditorInitial(res.data);
                  setEditorOpen(true);
                }
              }}
            >
              🎨 Modifica lavagna
            </button>
          )}
        </div>

        <Suspense fallback={null}>
          {setupOpen && (
            <TacticalBoardSetupModal
              isOpen={setupOpen}
              onClose={() => setSetupOpen(false)}
              onConfirm={(initial) => {
                setEditorInitial(initial);
                setSetupOpen(false);
                setEditorOpen(true);
              }}
            />
          )}
          {editorOpen && editorInitial && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
              <div style={{ width: '100%', maxWidth: 1280, height: '90vh', background: 'var(--c-card-bg)', borderRadius: 8 }}>
                <TacticalBoardEditor
                  initial={editorInitial}
                  blockId={block?._id}
                  onSaved={(saved) => {
                    setTacticalSchemaId(saved._id);
                    setEditorOpen(false);
                  }}
                  onClose={() => setEditorOpen(false)}
                />
              </div>
            </div>
          )}
        </Suspense>

        <div className={styles.divider} />

        {/* Esercizi */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <p className={styles.sectionLabel}>Esercizi</p>
            <span className={styles.sectionCount}>{exercises.length}</span>
          </div>

          <div className={styles.exercisesList}>
            {exercises.map((ex, i) => (
              <div key={i} className={styles.exerciseCard}>
                <div className={styles.exerciseTopRow}>
                  <div className={styles.exerciseLeftHandle}>
                    <span className={styles.exerciseOrder}>{i + 1}</span>
                  </div>

                  <div className={styles.exerciseSearchWrapper}>
                    <select
                      className={styles.exerciseSelect}
                      value={ex.fieldExerciseId ?? ''}
                      onChange={(e) =>
                        updateExercise(i, {
                          fieldExerciseId: e.target.value || undefined,
                          customName: e.target.value ? undefined : ex.customName,
                        })
                      }
                    >
                      <option value="">— Inline (custom) —</option>
                      {exercisesQ.data?.map((fe) => (
                        <option key={fe._id} value={fe._id}>
                          {fe.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="button"
                    className={styles.deleteExerciseBtn}
                    onClick={() => removeExercise(i)}
                    title="Rimuovi esercizio"
                  >
                    <TrashSimple size={15} />
                  </button>
                </div>

                {!ex.fieldExerciseId && (
                  <div className={styles.exerciseParamsRow}>
                    <div className={styles.paramField} style={{ flex: 2 }}>
                      <label className={styles.paramLabel}>Nome custom</label>
                      <input
                        className={styles.paramInput}
                        placeholder="Es. Possesso 4v4"
                        value={ex.customName ?? ''}
                        onChange={(e) => updateExercise(i, { customName: e.target.value })}
                      />
                    </div>
                  </div>
                )}

                <div className={styles.exerciseParamsRow}>
                  <div className={styles.paramField}>
                    <label className={styles.paramLabel}>Durata (min)</label>
                    <input
                      type="number"
                      min={0}
                      className={styles.paramInput}
                      placeholder="—"
                      value={ex.durationMinutes ?? ''}
                      onChange={(e) =>
                        updateExercise(i, {
                          durationMinutes: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                    />
                  </div>

                  <div className={styles.paramField}>
                    <label className={styles.paramLabel}>Intensità (1-10)</label>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      className={styles.paramInput}
                      placeholder="—"
                      value={ex.intensity ?? ''}
                      onChange={(e) =>
                        updateExercise(i, {
                          intensity: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                    />
                  </div>

                  <div className={styles.paramField} style={{ minWidth: 150 }}>
                    <label className={styles.paramLabel}>Organizzazione</label>
                    <input
                      className={styles.paramInput}
                      placeholder="Es. 4v4"
                      value={ex.organization ?? ''}
                      onChange={(e) => updateExercise(i, { organization: e.target.value })}
                    />
                  </div>

                  <div className={styles.paramField} style={{ flex: 2 }}>
                    <label className={styles.paramLabel}>Note</label>
                    <input
                      className={styles.paramInput}
                      placeholder="Note aggiuntive..."
                      value={ex.notes ?? ''}
                      onChange={(e) => updateExercise(i, { notes: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            className={styles.addExerciseBtn}
            onClick={addExerciseRow}
          >
            <Plus size={14} weight="bold" />
            Aggiungi esercizio
          </button>
        </div>
      </div>
    </GenericModalV2>
  );
};

export default FieldBlockBuilderModal;
